import React, { useCallback, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  IconButton,
  Chip,
  Switch,
  FormControlLabel,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Add, CheckCircle, Cancel, Edit, Delete } from "@mui/icons-material";
import CustomFormDialog, { FormField } from "../../components/CustomFormDialog/CustomFormDialog";
import CustomPopup from "../../components/CustomPopup/CustomPopup";
import Table, { Column } from "../../components/Table/Table";
import { useAuth } from "../../contexts/AuthContext";
import { useAlert } from "../../contexts/AlertContext";
import { useServerSidePagination } from "../../hooks/useServerSidePagination";
import { usersApi, UserApiResponse } from "../../services/usersApi";
import {
  accessRequestsApi,
  AccessRequestItem,
} from "../../services/accessRequestsApi";
import { register, RegisterRequest } from "../../services/authApi";
import { PaginatedResponse } from "../../types/api";
import { useTranslation } from "react-i18next";
import {
  categoriesApi,
  CategoryApiResponse,
  CategoryCreateRequest,
} from "../../services/categoriesApi";
import {
  getWarehousesAdminList,
  patchWarehouseAutoSuggest,
  WarehouseAdminRow,
} from "../../services/locationApi";
import { useWarehouse } from "../../contexts/WarehouseContext";
import Labels from "../Labels/Labels";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function formatUserName(row: UserApiResponse): string {
  if (row.name) return row.name;
  if (row.full_name) return row.full_name;
  const parts = [row.first_name, row.last_name].filter(Boolean);
  return parts.length ? parts.join(" ") : "—";
}

const AdminPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null);
  const [accessRequestsKey, setAccessRequestsKey] = useState(0);

  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { showAlert } = useAlert();
  const { refreshWarehouses } = useWarehouse();
  const canCreateUser = hasPermission("users.create");
  const canEditWarehouseSettings = hasPermission("locations.edit");

  type UserFormData = RegisterRequest & { password_confirm: string };

  const fetchUsers = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<UserApiResponse>> => {
      return usersApi.getUsers({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
      });
    },
    []
  );

  const usersPagination = useServerSidePagination(fetchUsers, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (err: any) => {
      showAlert(
        "error",
        err?.response?.data?.detail || t("adminPanel.messages.fetchUsersError")
      );
    },
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAddUser = async (formData: UserFormData) => {
    try {
      await register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        name: formData.name || undefined,
        phone: formData.phone || undefined,
      });
      showAlert("success", t("adminPanel.messages.userCreated"));
      setIsUserDialogOpen(false);
      usersPagination.refresh();
    } catch (err: any) {
      const msg =
        err?.response?.data?.username?.[0] ||
        err?.response?.data?.email?.[0] ||
        err?.response?.data?.password?.[0] ||
        err?.response?.data?.detail ||
        t("adminPanel.messages.userCreateError");
      showAlert("error", msg);
    }
  };

  const handleApprove = async (id: number) => {
    try {
      await accessRequestsApi.approveAccessRequest(id);
      showAlert("success", t("adminPanel.messages.requestApproved"));
      setAccessRequestsKey((k) => k + 1);
    } catch (err: any) {
      showAlert(
        "error",
        err?.response?.data?.detail || t("adminPanel.messages.approveError")
      );
    }
  };

  const handleDeny = async (id: number, notes?: string) => {
    try {
      await accessRequestsApi.denyAccessRequest(id, notes);
      showAlert("success", t("adminPanel.messages.requestDenied"));
      setDenyDialogOpen(false);
      setSelectedRequestId(null);
      setAccessRequestsKey((k) => k + 1);
    } catch (err: any) {
      showAlert(
        "error",
        err?.response?.data?.detail || t("adminPanel.messages.denyError")
      );
    }
  };

  const userFormFields: FormField[] = [
    { name: "username", label: t("adminPanel.form.username"), required: true },
    { name: "email", label: t("adminPanel.form.email"), type: "email", required: true },
    { name: "password", label: t("adminPanel.form.password"), type: "password", required: true },
    { name: "password_confirm", label: t("adminPanel.form.passwordConfirm"), type: "password", required: true },
    { name: "name", label: t("adminPanel.form.name"), required: false },
    { name: "phone", label: t("adminPanel.form.phone"), required: false },
  ];

  const initialUserForm: UserFormData = {
    username: "",
    email: "",
    password: "",
    password_confirm: "",
    name: "",
    phone: "",
  };

  const validateUser = (data: UserFormData) => {
    const errors: Record<string, string> = {};
    if (!data.username?.trim()) errors.username = t("adminPanel.validation.usernameRequired");
    if (!data.email?.trim()) errors.email = t("adminPanel.validation.emailRequired");
    else if (!/\S+@\S+\.\S+/.test(data.email)) errors.email = t("adminPanel.validation.invalidEmail");
    if (!data.password) errors.password = t("adminPanel.validation.passwordRequired");
    else if (data.password.length < 8) errors.password = t("adminPanel.validation.passwordMinLength");
    if (!data.password_confirm) errors.password_confirm = t("adminPanel.validation.passwordConfirmRequired");
    else if (data.password !== data.password_confirm) errors.password_confirm = t("adminPanel.validation.passwordMismatch");
    return errors;
  };

  const userColumns: Column<UserApiResponse>[] = [
    { id: "username", label: t("adminPanel.columns.username"), flex: 1 },
    {
      id: "name",
      label: t("adminPanel.columns.name"),
      flex: 1,
      format: (_, row) => formatUserName(row),
    },
    { id: "email", label: t("adminPanel.columns.email"), flex: 1 },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label={t("adminPanel.adminPanel")}>
          <Tab label={t("adminPanel.users")} />
          <Tab label={t("adminPanel.accessApprovals")} />
          <Tab label={t("adminPanel.categories")} />
          <Tab label={t("navigation.labels")} />
          <Tab label={t("adminPanel.warehouseTab")} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h5" component="h2">
            {t("adminPanel.userManagement")}
          </Typography>
          {canCreateUser && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setIsUserDialogOpen(true)}
            >
              {t("adminPanel.addUser")}
            </Button>
          )}
        </Box>
        <Table
          columns={userColumns}
          data={usersPagination.data}
          page={usersPagination.page}
          rowsPerPage={usersPagination.pageSize}
          totalRows={usersPagination.totalCount}
          onPageChange={usersPagination.handlePageChange}
          onRowsPerPageChange={usersPagination.handlePageSizeChange}
          rowKey="id"
          loading={usersPagination.isLoading}
          searchable
          searchPlaceholder={t("adminPanel.searchUsers")}
          onSearchChange={usersPagination.handleSearchChange}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <AccessRequestsTab
          key={accessRequestsKey}
          onApprove={handleApprove}
          onDenyClick={(id) => {
            setSelectedRequestId(id);
            setDenyDialogOpen(true);
          }}
          showAlert={showAlert}
        />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <CategoriesTab showAlert={showAlert} />
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Labels />
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <WarehouseSettingsTab
          showAlert={showAlert}
          canEdit={canEditWarehouseSettings}
          onSaved={refreshWarehouses}
        />
      </TabPanel>

      <CustomFormDialog<UserFormData>
        open={isUserDialogOpen}
        onClose={() => setIsUserDialogOpen(false)}
        onSubmit={handleAddUser}
        title={t("adminPanel.addUserTitle")}
        submitButtonText={t("adminPanel.addUser")}
        cancelButtonText={t("common.cancel")}
        formFields={userFormFields}
        initialData={initialUserForm}
        validate={validateUser}
      />

      {denyDialogOpen && selectedRequestId !== null && (
        <CustomFormDialog<{ review_notes: string }>
          open={denyDialogOpen}
          onClose={() => {
            setDenyDialogOpen(false);
            setSelectedRequestId(null);
          }}
          onSubmit={(formData) => {
            handleDeny(selectedRequestId, formData.review_notes);
          }}
          title={t("adminPanel.denyRequestTitle")}
          submitButtonText={t("adminPanel.deny")}
          cancelButtonText={t("common.cancel")}
          formFields={[
            {
              name: "review_notes",
              label: t("adminPanel.reviewNotesLabel"),
              required: false,
              multiline: true,
              rows: 3,
            },
          ]}
          initialData={{ review_notes: "" }}
        />
      )}
    </Box>
  );
};

interface AccessRequestsTabProps {
  onApprove: (id: number) => void;
  onDenyClick: (id: number) => void;
  showAlert: (type: "success" | "error" | "warning", message: string, duration?: number) => void;
}

function AccessRequestsTab({
  onApprove,
  onDenyClick,
  showAlert,
}: AccessRequestsTabProps) {
  const { t } = useTranslation();
  const [requests, setRequests] = useState<AccessRequestItem[]>([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await accessRequestsApi.getAccessRequests({ status: "pending" });
      setRequests(Array.isArray(data) ? data : []);
    } catch (err: any) {
      showAlert(
        "error",
        err?.response?.data?.detail || t("adminPanel.messages.fetchRequestsError")
      );
    } finally {
      setLoading(false);
    }
  }, [showAlert, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  const columns: Column<AccessRequestItem>[] = [
    { id: "user_username", label: t("adminPanel.columns.user"), flex: 1 },
    {
      id: "user_email",
      label: t("adminPanel.columns.email"),
      flex: 1,
      format: (v: string) => v || "—",
    },
    {
      id: "requested_at",
      label: t("adminPanel.columns.date"),
      flex: 1,
      format: (v: string) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      id: "ip_address",
      label: t("adminPanel.columns.ip"),
      flex: 0.8,
      format: (v: string) => v || "—",
    },
    {
      id: "browser",
      label: t("adminPanel.columns.browser"),
      flex: 0.8,
      format: (v: string) => v || "—",
    },
    {
      id: "os",
      label: t("adminPanel.columns.os"),
      flex: 0.8,
      format: (v: string) => v || "—",
    },
    {
      id: "is_new_device",
      label: t("adminPanel.columns.newDeviceIp"),
      flex: 1,
      format: (_, row) => {
        const newDevice = row.is_new_device;
        const newIp = row.is_new_ip;
        if (newDevice && newIp) return <Chip size="small" label={t("adminPanel.chips.newDeviceAndIp")} color="warning" />;
        if (newDevice) return <Chip size="small" label={t("adminPanel.chips.newDevice")} color="warning" />;
        if (newIp) return <Chip size="small" label={t("adminPanel.chips.newIp")} color="info" />;
        return "—";
      },
    },
    {
      id: "id",
      label: t("adminPanel.columns.actions"),
      flex: 1,
      renderCell: (params: any) => {
        const row = params?.row as AccessRequestItem;
        if (!row) return null;
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              color="primary"
              size="small"
              onClick={() => onApprove(row.id)}
              title={t("adminPanel.actions.approve")}
            >
              <CheckCircle />
            </IconButton>
            <IconButton
              color="error"
              size="small"
              onClick={() => onDenyClick(row.id)}
              title={t("adminPanel.actions.deny")}
            >
              <Cancel />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
        {t("adminPanel.accessApprovals")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {t("adminPanel.accessApprovalsDescription")}
      </Typography>
      <Table<AccessRequestItem>
        columns={columns}
        data={requests}
        page={0}
        rowsPerPage={requests.length || 10}
        totalRows={requests.length}
        onPageChange={() => { }}
        onRowsPerPageChange={() => { }}
        rowKey="id"
        loading={loading}
        emptyMessage={t("adminPanel.emptyAccessRequests")}
      />
    </>
  );
}

interface CategoriesTabProps {
  showAlert: (type: "success" | "error" | "warning", message: string, duration?: number) => void;
}

function CategoriesTab({ showAlert }: CategoriesTabProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryApiResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryApiResponse | null>(null);

  const fetchCategories = useCallback(
    async (
      page: number,
      pageSize: number,
      search: string
    ): Promise<PaginatedResponse<CategoryApiResponse>> => {
      return categoriesApi.getCategories({
        page: page + 1,
        page_size: pageSize,
        search: search || undefined,
      });
    },
    []
  );

  const pagination = useServerSidePagination(fetchCategories, {
    initialPageSize: 10,
    debounceMs: 500,
    onError: (err: any) => {
      showAlert(
        "error",
        err?.response?.data?.detail || t("adminPanel.messages.fetchCategoriesError")
      );
    },
  });

  const handleAdd = async (formData: CategoryCreateRequest) => {
    try {
      await categoriesApi.createCategory(formData);
      showAlert("success", t("adminPanel.messages.categoryCreated"));
      setDialogOpen(false);
      setEditingCategory(null);
      pagination.refresh();
    } catch (err: any) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        t("adminPanel.messages.categoryCreateError");
      showAlert("error", msg);
    }
  };

  const handleEdit = async (formData: CategoryCreateRequest) => {
    if (!editingCategory) return;
    try {
      await categoriesApi.updateCategory(editingCategory.id, formData);
      showAlert("success", t("adminPanel.messages.categoryUpdated"));
      setDialogOpen(false);
      setEditingCategory(null);
      pagination.refresh();
    } catch (err: any) {
      const msg =
        err?.response?.data?.name?.[0] ||
        err?.response?.data?.detail ||
        t("adminPanel.messages.categoryUpdateError");
      showAlert("error", msg);
    }
  };

  const handleDelete = async () => {
    if (!selectedCategory) return;
    try {
      await categoriesApi.deleteCategory(selectedCategory.id);
      showAlert("success", t("adminPanel.messages.categoryDeleted"));
      setDeleteDialogOpen(false);
      setSelectedCategory(null);
      pagination.refresh();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail ||
        t("adminPanel.messages.categoryDeleteError");
      showAlert("error", msg);
    }
  };

  const columns: Column<CategoryApiResponse>[] = [
    { id: "name", label: t("adminPanel.categoryColumns.name"), flex: 1.5 },
    {
      id: "description",
      label: t("adminPanel.categoryColumns.description"),
      flex: 2,
      format: (v: string) => v || "—",
    },
    {
      id: "created_at",
      label: t("adminPanel.categoryColumns.createdAt"),
      flex: 1,
      format: (v: string) => (v ? new Date(v).toLocaleDateString() : "—"),
    },
    {
      id: "created_by_name" as keyof CategoryApiResponse,
      label: t("adminPanel.categoryColumns.createdBy"),
      flex: 1,
      format: (v: string) => v || "—",
    },
    {
      id: "id" as keyof CategoryApiResponse,
      label: t("common.actions"),
      flex: 1,
      align: "center",
      sortable: false,
      format: (_, row) => (
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
          <IconButton
            size="small"
            color="primary"
            title={t("common.edit")}
            onClick={(e) => {
              e.stopPropagation();
              setEditingCategory(row);
              setDialogOpen(true);
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="primary"
            title={t("common.delete")}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCategory(row);
              setDeleteDialogOpen(true);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const formFields: FormField[] = [
    { name: "name", label: t("adminPanel.categoryForm.name"), required: true },
    {
      name: "description",
      label: t("adminPanel.categoryForm.description"),
      required: false,
      multiline: true,
      rows: 3,
    },
  ];

  const validateCategory = (data: CategoryCreateRequest) => {
    const errors: Record<string, string> = {};
    if (!data.name?.trim()) errors.name = t("adminPanel.validation.categoryNameRequired");
    return errors;
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" component="h2">
          {t("adminPanel.categoryManagement")}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingCategory(null);
            setDialogOpen(true);
          }}
        >
          {t("adminPanel.addCategory")}
        </Button>
      </Box>
      <Table
        columns={columns}
        data={pagination.data}
        page={pagination.page}
        rowsPerPage={pagination.pageSize}
        totalRows={pagination.totalCount}
        onPageChange={pagination.handlePageChange}
        onRowsPerPageChange={pagination.handlePageSizeChange}
        rowKey="id"
        loading={pagination.isLoading}
        searchable
        searchPlaceholder={t("adminPanel.searchCategories")}
        onSearchChange={pagination.handleSearchChange}
      />
      <CustomFormDialog<CategoryCreateRequest>
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingCategory(null);
        }}
        onSubmit={editingCategory ? handleEdit : handleAdd}
        title={editingCategory ? t("adminPanel.editCategory") : t("adminPanel.addCategoryTitle")}
        submitButtonText={editingCategory ? t("common.saveChanges") : t("common.add")}
        cancelButtonText={t("common.cancel")}
        formFields={formFields}
        initialData={
          editingCategory
            ? { name: editingCategory.name, description: editingCategory.description }
            : { name: "", description: "" }
        }
        validate={validateCategory}
      />
      <CustomPopup
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedCategory(null);
        }}
        title={t("adminPanel.deleteCategoryTitle")}
        text={t("adminPanel.deleteCategoryWarning")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={handleDelete}
      />
    </>
  );
}

interface WarehouseSettingsTabProps {
  showAlert: (type: "success" | "error" | "warning", message: string, duration?: number) => void;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}

function WarehouseSettingsTab({ showAlert, canEdit, onSaved }: WarehouseSettingsTabProps) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<WarehouseAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWarehousesAdminList();
      setRows(data);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string; error?: string } } };
      showAlert(
        "error",
        ax?.response?.data?.detail ||
          ax?.response?.data?.error ||
          t("adminPanel.messages.fetchWarehousesError")
      );
    } finally {
      setLoading(false);
    }
  }, [showAlert, t]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (row: WarehouseAdminRow, checked: boolean) => {
    if (!canEdit) return;
    setSavingId(row.id);
    try {
      await patchWarehouseAutoSuggest(row.id, checked);
      setRows((prev) =>
        prev.map((r) => (r.id === row.id ? { ...r, auto_suggest_locations: checked } : r))
      );
      showAlert("success", t("adminPanel.messages.warehouseSettingsSaved"));
      await onSaved();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { detail?: string; error?: string } } };
      showAlert(
        "error",
        ax?.response?.data?.detail ||
          ax?.response?.data?.error ||
          t("adminPanel.messages.warehouseSettingsError")
      );
    } finally {
      setSavingId(null);
    }
  };

  return (
    <>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        {t("adminPanel.warehouseSettingsTitle")}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t("adminPanel.warehouseSettingsDescription")}
      </Typography>
      {!canEdit && (
        <Typography color="warning.main" sx={{ mb: 2 }}>
          {t("adminPanel.warehouseSettingsNoPermission")}
        </Typography>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          {rows.map((row) => (
            <Paper
              key={row.id}
              elevation={0}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "rgba(255,255,255,0.03)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={row.auto_suggest_locations}
                      onChange={(_, c) => void handleToggle(row, c)}
                      disabled={!canEdit || savingId === row.id}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle1" color="text.primary">
                        {row.name}
                      </Typography>
                      {row.city ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.city}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                />
                {savingId === row.id ? <CircularProgress size={22} /> : null}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 0.5 }}>
                {t("adminPanel.autoSuggestHelp")}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}
    </>
  );
}

export default AdminPanel;
